import { Component, Event, EventEmitter, h, Method, Prop } from "@stencil/core";
import { Table } from "../table";
import { TableCell } from "../table-cell/table-cell";
import { TableHeader } from "../table-header/table-header";
import { Column, TableRowWithId } from "../table-interfaces";

@Component({
    tag: "uic-table-column",
    styleUrl: "table-column.scss",
    shadow: false
})
export class TableColumn implements Column {
    /** defines which field of the table will be displayed in the column */
    @Prop() readonly field: string;
    /** defines the label of the table column */
    @Prop() readonly label: string;
    /** defines if the column should be a group */
    @Prop() readonly group = false;
    /** defines if the column should be sortable */
    @Prop() readonly sortable = false;
    /** defines the sort field name, if set, instead of using the column field */
    @Prop() readonly sortField?: string;
    /** wrap the content of this column if required, requires to set a max-width on table (e.g. using `full-width`
     *  or css prop `--uic-table-width`) or column */
    @Prop() readonly multiline = false;
    /** defines if the column should be filterable */
    @Prop() readonly filterable: boolean = false;
    /**determines whether empty and double values should be filtered*/
    @Prop() readonly filterEmptyAndDuplicateItems: boolean = false;
    /**
     * defines the items which are displayed in the filter component
     * the column uses the internal column content, if this is not set
     **/
    @Prop() readonly customFilterItems?: [];
    /** make the render function public */
    @Method()
    async getColumnMethods() {
        return {
            renderHeader: this.renderHeader.bind(this),
            renderCell: this.renderCell.bind(this),
            getSortString: this.getSortString.bind(this)
        };
    }
    // eslint-disable-next-line
    private getSortString(row: any) {
        const sortField = this.sortField || this.field;
        if (sortField === "" || !row[sortField]) {
            return "";
        }
        return row[sortField] + "";
    }
    /**when the sort Button is clicked it emits the asc or desc name  */
    @Event({
        eventName: "uicTableFilter",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    filterEvent: EventEmitter;

    private renderHeader({ initialSortingCriterion: sortCriterion, data: rows = [] }: Table) {
        const sortData =
            sortCriterion?.sortProperty === this.sortField || sortCriterion?.sortProperty === this.field
                ? sortCriterion
                : undefined;

        return (
            <TableHeader
                label={this.label}
                sortable={this.sortable}
                sortField={this.sortField || this.field}
                field={this.field}
                group={this.group}
                testId={this.field}
                initialSortDirection={sortData?.direction ?? ""}
                filterable={this.filterable}
                filterEmptyAndDuplicateItems={this.filterEmptyAndDuplicateItems}
                filterItems={this.customFilterItems ? this.customFilterItems : (rows as [])}
            />
        );
    }

    /** render the cell for the column */
    private renderCell(row: TableRowWithId) {
        const { field, group, multiline } = this;
        const empty = field === "" || !row[field];
        return (
            <TableCell group={group} empty={empty} multiline={multiline} testId={field}>
                <span>{!empty && row[field]}</span>
            </TableCell>
        ); // the span is needed because Stencil does not accept string contents here
    }
}
